import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { isEmpty } from 'lodash-es'
import { AxiosError } from 'axios'
import MToast from '@/utils/modal/MToast'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { MCDShipperCreateForm, mcdShipperCreateSchema, MCDShipperUpdateForm, mcdShipperUpdateSchema } from '@/services/shipper/schemas'
import { ShipperItem } from '@/services/shipper/types'
import { useCreateMCDShipper, useUpdateMCDShipper } from '@/services/shipper/useShipperQueries'

const defaultValues: MCDShipperCreateForm = {
  logisticsServiceType: 'MCD',
  shipperName: '',
  companyName: '',
  businessRegistrationNumber: '',
  ownerName: '',
  businessType: '',
  businessMajor: '',
  zipCode: '',
  address: '',
  phoneNumber: '',
} as MCDShipperCreateForm

export function useMCDShipperCreateForm(onRefresh: () => Promise<void>, onClose: () => void) {
  const createMCDShipper = useCreateMCDShipper()

  const form = useForm<MCDShipperCreateForm>({
    resolver: zodResolver(mcdShipperCreateSchema) as any,
    defaultValues,
    mode: 'onChange',
  })

  const isValid = mcdShipperCreateSchema.safeParse(form.watch()).success && isEmpty(form.formState.errors)

  const handleSubmit = async () => {
    try {
      const formData = form.getValues()

      const transformedFormData = {
        ...formData,
        // NOTE: 추후 추가될 여지가 있어 필드 만들어두었다고 함. null로 보내줘야 함.
        // LINK: https://musinsa.slack.com/archives/C08M9E78B3N/p1750665871122339?thread_ts=1750641682.987349&cid=C08M9E78B3N
        salesPlatforms: null,
      }

      await createMCDShipper.mutateAsync(transformedFormData as any)

      MToast.show('화주사가 추가되었습니다.')
      setTimeout(() => {
        form.reset()

        onRefresh()
        onClose()
      }, 100)
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, 'MWP 화주 추가에 실패했습니다.'))
    }
  }

  return {
    form,
    isValid,
    handleSubmit,
  }
}

export function useMCDShipperUpdateForm(shipperData: ShipperItem, onRefresh: () => Promise<void>, onClose: () => void) {
  const updateMCDShipper = useUpdateMCDShipper()

  const updateDefaultValues: MCDShipperUpdateForm = {
    logisticsServiceType: 'MCD',
    shipperName: shipperData.shipperName,
    companyName: shipperData.companyName,
    shipperCode: shipperData.shipperCode,
    businessRegistrationNumber: shipperData.businessRegistrationNumber,
    ownerName: shipperData.ownerName,
    businessType: shipperData.businessType || '',
    businessMajor: shipperData.businessMajor || '',
    zipCode: shipperData.zipCode || '',
    address: shipperData.address || '',
    phoneNumber: shipperData.phoneNumber || '',
  }

  const form = useForm<MCDShipperUpdateForm>({
    resolver: zodResolver(mcdShipperUpdateSchema) as any,
    defaultValues: updateDefaultValues,
    mode: 'onChange',
  })

  const isValid = mcdShipperUpdateSchema.safeParse(form.watch()).success && isEmpty(form.formState.errors)

  const handleSubmit = async () => {
    try {
      const formData = form.getValues()

      const transformedFormData = {
        ...formData,

        // NOTE: 추후 추가될 여지가 있어 필드 만들어두었다고 함. null로 보내줘야 함.
        // LINK: https://musinsa.slack.com/archives/C08M9E78B3N/p1750665871122339?thread_ts=1750641682.987349&cid=C08M9E78B3N
        salesPlatforms: null,
      }

      await updateMCDShipper.mutateAsync(transformedFormData as any)

      // MCD 수정 API 호출 (추후 구현)
      MToast.show('MCD 화주 수정이 완료되었습니다.')
      setTimeout(() => {
        onRefresh()
        onClose()
      }, 100)
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, 'MCD 화주 수정에 실패했습니다.'))
    }
  }

  return {
    form,
    isValid,
    handleSubmit,
  }
}
